import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, History, Loader2 } from 'lucide-react';

import {
  reviewService,
  type LearningEpisodeParticleHistoryEntry,
} from '../../services/reviewService';

type PasSheetFeedbackBannerProps = {
  userId: string;
  kpId?: string | null;
  sessionId?: string | null;
  /** `toolbar`：顶部工具栏按钮 + 下拉面板；`banner`：画布上方横幅（已弃用，保留兼容） */
  variant?: 'banner' | 'toolbar';
};

type FeedbackItem = {
  key: string;
  sessionId: string;
  score: number | null;
  feedback: string | null;
  submittedAt: string | null;
};

type UiLocale = 'zh-Hans' | 'en';

const EN_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const REVIEW_FEEDBACK_ENUM_MARKERS = new Set([
  'proficient',
  'fuzzy',
  'forgotten',
  'passed',
  'visited',
]);

function resolveUiLocale(): UiLocale {
  if (typeof navigator === 'undefined') return 'zh-Hans';
  return String(navigator.language || '').toLowerCase().startsWith('en') ? 'en' : 'zh-Hans';
}

function parseTimeMs(value?: string | null) {
  const parsed = value ? Date.parse(value) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : 0;
}

function coerceFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return null;
}

function sanitizeSummaryFeeling(value: unknown): string | null {
  const text = String(value || '').trim();
  if (!text) return null;
  if (REVIEW_FEEDBACK_ENUM_MARKERS.has(text.toLowerCase())) return null;
  return text;
}

function formatFeedbackSubmittedAt(iso: string | null | undefined, locale: UiLocale) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (value: number) => String(value).padStart(2, '0');
  const day = date.getDate();
  const time = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  if (locale === 'en') return `${EN_MONTHS[date.getMonth()]} ${day} ${time}`;
  return `${date.getMonth() + 1}月${day}日 ${time}`;
}

function collectFamilySummariesForPasSheet(
  entries: LearningEpisodeParticleHistoryEntry[] | null | undefined,
): FeedbackItem[] {
  const list = Array.isArray(entries) ? entries : [];
  const withSummary = list
    .map((entry) => {
      const summary = entry.familySummary;
      if (!summary) return null;
      const feedback = sanitizeSummaryFeeling(summary.feedback);
      const score = coerceFiniteNumber(summary.score);
      if (feedback == null && score == null && !summary.submittedAt) return null;
      return {
        entry,
        score,
        feedback,
        submittedAt: summary.submittedAt || entry.completedAt || entry.updatedAt || null,
        rank: (feedback ? 4 : 0) + (score != null ? 2 : 0) + (summary.submittedAt ? 1 : 0),
      };
    })
    .filter((item): item is NonNullable<typeof item> => Boolean(item));

  const byKey = new Map<string, (typeof withSummary)[number]>();
  for (const item of withSummary) {
    const key = String(item.entry.sessionId || '').trim()
      || `at:${item.submittedAt || item.entry.updatedAt}`;
    const prev = byKey.get(key);
    if (!prev) {
      byKey.set(key, item);
      continue;
    }
    const better = (
      item.rank - prev.rank
      || parseTimeMs(item.submittedAt) - parseTimeMs(prev.submittedAt)
      || parseTimeMs(item.entry.completedAt || item.entry.updatedAt)
      - parseTimeMs(prev.entry.completedAt || prev.entry.updatedAt)
    ) >= 0 ? item : prev;
    byKey.set(key, better);
  }

  return [...byKey.values()]
    .sort((left, right) => (
      parseTimeMs(right.submittedAt) - parseTimeMs(left.submittedAt)
      || parseTimeMs(right.entry.completedAt || right.entry.updatedAt)
      - parseTimeMs(left.entry.completedAt || left.entry.updatedAt)
    ))
    .map((item) => ({
      key: String(item.entry.sessionId || item.submittedAt || item.entry.updatedAt),
      sessionId: String(item.entry.sessionId || ''),
      score: item.score,
      feedback: item.feedback,
      submittedAt: item.submittedAt,
    }));
}

function formatScoreLine(score: number | null, locale: UiLocale) {
  if (typeof score !== 'number') return '';
  return locale === 'en' ? `${Math.round(score)}` : `${Math.round(score)} 分`;
}

function formatMetaLine(item: FeedbackItem, locale: UiLocale) {
  const parts: string[] = [];
  const scoreLine = formatScoreLine(item.score, locale);
  if (scoreLine) parts.push(scoreLine);
  parts.push(item.feedback || (locale === 'en' ? 'Undefined' : '未定义'));
  const submittedLabel = formatFeedbackSubmittedAt(item.submittedAt, locale);
  if (submittedLabel) parts.push(submittedLabel);
  return parts.join(' · ');
}

function FeedbackList({
  items,
  uiLocale,
  currentSessionId,
}: {
  items: FeedbackItem[];
  uiLocale: UiLocale;
  currentSessionId: string;
}) {
  return (
    <div className="max-h-56 divide-y divide-border/70 overflow-y-auto">
      {items.map((item, index) => {
        const scoreLine = formatScoreLine(item.score, uiLocale);
        const submittedLabel = formatFeedbackSubmittedAt(item.submittedAt, uiLocale);
        const feelingLine = item.feedback || (uiLocale === 'en' ? 'Undefined' : '未定义');
        const nth = items.length - index;
        const isCurrent = Boolean(currentSessionId && item.sessionId === currentSessionId);
        return (
          <div key={item.key} className="space-y-1 px-3 py-2.5">
            <div className="flex items-center gap-2 text-[11px] font-bold text-muted">
              <span>{uiLocale === 'en' ? `Review ${nth}` : `第 ${nth} 次`}</span>
              {isCurrent ? (
                <span className="rounded-full bg-primary/10 px-1.5 py-px text-[10px] text-primary-strong">
                  {uiLocale === 'en' ? 'Current' : '当前'}
                </span>
              ) : null}
            </div>
            {scoreLine ? <div className="text-xs font-bold text-primary-strong">{scoreLine}</div> : null}
            {submittedLabel ? <div className="text-[11px] text-muted">{submittedLabel}</div> : null}
            <p className={item.feedback ? 'whitespace-pre-wrap text-sm leading-6 text-ink' : 'text-xs text-muted'}>
              {feelingLine}
            </p>
          </div>
        );
      })}
    </div>
  );
}

export function PasSheetFeedbackBanner({
  userId,
  kpId,
  sessionId,
  variant = 'banner',
}: PasSheetFeedbackBannerProps) {
  const uiLocale = useMemo(() => resolveUiLocale(), []);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const normalizedUserId = String(userId || '').trim();
    const normalizedKpId = String(kpId || '').trim();
    if (!normalizedUserId || !normalizedKpId) {
      setItems([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    void reviewService.getKnowledgeParticleReviewHistory(normalizedKpId, normalizedUserId)
      .then((entries) => {
        if (cancelled) return;
        setItems(collectFamilySummariesForPasSheet(entries));
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [kpId, userId]);

  const latest = items[0] || null;
  const subtitleLine = useMemo(() => {
    if (!latest) return '';
    const parts = [formatMetaLine(latest, uiLocale)];
    if (items.length > 1) {
      parts.push(uiLocale === 'en' ? `${items.length} reviews` : `共 ${items.length} 次`);
    }
    return parts.join(' · ');
  }, [items.length, latest, uiLocale]);
  const currentSessionId = String(sessionId || '').trim();

  useEffect(() => {
    if (variant !== 'toolbar' || !expanded) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      const root = rootRef.current;
      if (!root) return;
      if (event.target instanceof Node && root.contains(event.target)) return;
      setExpanded(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [expanded, variant]);

  if (!String(kpId || '').trim()) return null;
  if (!loading && !items.length) return null;

  if (variant === 'toolbar') {
    return (
      <div ref={rootRef} className="relative">
        <button
          type="button"
          disabled={loading || !items.length}
          onClick={() => {
            if (loading || !items.length) return;
            setExpanded((prev) => !prev);
          }}
          className={
            'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition disabled:opacity-40 sm:px-4 sm:py-2 sm:text-sm '
            + (expanded
              ? 'border-primary-strong bg-primary/10 text-primary-strong'
              : 'border-border bg-surface text-ink hover:bg-canvas')
          }
          aria-expanded={expanded}
          aria-label="展开或收起近期复习反馈"
          title="近期复习反馈"
        >
          {loading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <History size={16} strokeWidth={2.2} />
          )}
          <span className="hidden sm:inline">近期复习反馈</span>
        </button>

        {expanded && items.length ? (
          <div className="absolute right-0 z-50 mt-2 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-border bg-surface shadow-card">
            <div className="border-b border-border/70 px-3 py-2.5">
              <div className="text-xs font-bold text-ink">近期复习反馈</div>
              {subtitleLine ? (
                <div className="mt-0.5 truncate text-[11px] text-muted">{subtitleLine}</div>
              ) : null}
            </div>
            <FeedbackList
              items={items}
              uiLocale={uiLocale}
              currentSessionId={currentSessionId}
            />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="mx-3 mt-2 mb-1 w-fit max-w-[calc(100%-1.5rem)] overflow-hidden rounded-xl border border-border/80 bg-surface/95 shadow-sm backdrop-blur sm:w-[calc(100%-1.5rem)]">
      <button
        type="button"
        disabled={loading || !items.length}
        onClick={() => {
          if (loading || !items.length) return;
          setExpanded((prev) => !prev);
        }}
        className="flex w-full items-center justify-center gap-2 px-2 py-2 text-left transition hover:bg-primary/5 disabled:opacity-70 sm:justify-start sm:px-3"
        aria-expanded={expanded}
        aria-label="展开或收起近期复习反馈"
        title="近期复习反馈"
      >
        <History size={16} className="shrink-0 text-primary-strong" strokeWidth={2.2} />
        <div className="hidden min-w-0 flex-1 sm:block">
          <div className="truncate text-xs font-bold text-ink">近期复习反馈</div>
          {loading ? (
            <div className="truncate text-[11px] text-muted">正在加载反馈…</div>
          ) : subtitleLine ? (
            <div className="truncate text-[11px] text-muted">{subtitleLine}</div>
          ) : null}
        </div>
        {loading ? (
          <Loader2 size={16} className="hidden animate-spin text-primary-strong sm:block" />
        ) : expanded ? (
          <ChevronUp size={18} className="hidden text-muted sm:block" />
        ) : (
          <ChevronDown size={18} className="hidden text-muted sm:block" />
        )}
      </button>

      {expanded && items.length ? (
        <div className="border-t border-border/70">
          <FeedbackList
            items={items}
            uiLocale={uiLocale}
            currentSessionId={currentSessionId}
          />
        </div>
      ) : null}
    </div>
  );
}
